import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ApplicationCard, APPLICATION_CARD_LIST_TEXT } from './components';
import { useGetApplicationCardListPageData, useGetChoiceData } from './graphql';
import { TokenType, useAuth } from '../../contexts';
import { Button, ErrorPage, Loading } from '../../components';
import { type ApplicationPageData } from './graphql/ApplicationCardList/types';
import { APPLICATION_STATUS_TYPE } from '../../utils';

export const ApplicationCardList: React.FunctionComponent = () => {
  const { user, isTokenValid } = useAuth();
  const isValid = isTokenValid();
  const applicant = user?.userInfo?.username;

  const isStaffView = user?.tokenType === TokenType.INTERNAL;

  if (isStaffView && isValid) {
    return <ErrorPage errorCode={403} />;
  }

  const dataList = useGetApplicationCardListPageData(applicant ?? '');

  const navigate = useNavigate();
  const { programmeChoiceMetadata, loading: choiceMetaLoading } =
    useGetChoiceData();
  const departments = programmeChoiceMetadata.departments;

  const redirectToApplicationCreation = (): void => {
    navigate(`/application/create`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  function getStatusPriority(status: APPLICATION_STATUS_TYPE): number {
    switch (status) {
      case APPLICATION_STATUS_TYPE.RETURNED:
        return -1;
      case APPLICATION_STATUS_TYPE.WITHDRAWN:
        return 1;
      default:
        return 0;
    }
  }

  dataList.sort((a: ApplicationPageData, b: ApplicationPageData) => {
    return getStatusPriority(a.status) - getStatusPriority(b.status);
  });

  return (
    <Container>
      <div className="application-title">
        <h1>{APPLICATION_CARD_LIST_TEXT.APPLICATION}</h1>
      </div>
      <div className="application-title">
        <Button
          color="primary"
          variant="contained"
          onClick={redirectToApplicationCreation}
        >
          {'Create Application'}
        </Button>
      </div>
      <CardsContent>
        {
          // TODO: The text will be extracted out
          choiceMetaLoading ? (
            <Loading />
          ) : (dataList ?? [])?.length === 0 ? (
            <h2 className="ml-4">
              {APPLICATION_CARD_LIST_TEXT.NO_APPLICATION}
            </h2>
          ) : (
            dataList?.map((data, index) => {
              const labelsForDepartment = departments
                ?.filter(department => department.value === data.title)
                ?.map(matchingDepartment => matchingDepartment.label);
              return (
                <ApplicationCard
                  key={index}
                  cardData={data}
                  deptLabel={labelsForDepartment[0]}
                />
              );
            })
          )
        }
      </CardsContent>
    </Container>
  );
};

const Container = styled.div`
  .application-title {
    h1 {
      font-size: 2rem;
      margin-right: 1rem;
    }

    margin: 2rem 4rem;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const CardsContent = styled.div`
  padding: 0 3rem;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`;
